import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import { calculateLoanSummary, calculateInvestmentGrowth, calculateMortgageExtras, LOAN_PRESETS } from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

const COMPARE_COLORS = ['#a855f7', '#22d3ee', '#34d399', '#fbbf24']

const emptyLoanScenario = () => ({
  id: Date.now(),
  name: '',
  type: 'mortgage',
  principal: 300000,
  rate: 6.5,
  termMonths: 360,
  downPayment: 0,
  downPaymentPercent: 0,
  downPaymentMode: 'dollar',
  extraPayment: 0,
  propertyTaxRate: 1.2,
  pmiRate: 0.75,
  annualInsurance: 1800,
  monthlyHOA: 0,
})

const emptyInvestmentScenario = () => ({
  id: Date.now(),
  name: '',
  initialAmount: 10000,
  monthlyContribution: 500,
  annualReturn: 7,
  years: 30,
})

export default function ComparisonTool() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [mode, setMode] = useState('loans')
  const chartRef = useRef(null)
  const [loanScenarios, setLoanScenarios] = useState([
    { ...emptyLoanScenario(), name: 'Scenario A' },
    { ...emptyLoanScenario(), id: Date.now() + 1, name: 'Scenario B', rate: 7.5 },
  ])
  const [investmentScenarios, setInvestmentScenarios] = useState([
    { ...emptyInvestmentScenario(), name: 'Conservative', annualReturn: 5 },
    { ...emptyInvestmentScenario(), id: Date.now() + 1, name: 'Aggressive', annualReturn: 10 },
  ])

  const scenarios = mode === 'loans' ? loanScenarios : investmentScenarios
  const setScenarios = mode === 'loans' ? setLoanScenarios : setInvestmentScenarios

  const addScenario = () => {
    if (scenarios.length >= 4) return
    const letter = String.fromCharCode(65 + scenarios.length)
    if (mode === 'loans') {
      setLoanScenarios(s => [...s, { ...emptyLoanScenario(), name: `Scenario ${letter}` }])
    } else {
      setInvestmentScenarios(s => [...s, { ...emptyInvestmentScenario(), name: `Scenario ${letter}` }])
    }
  }

  const removeScenario = (id) => {
    if (scenarios.length <= 2) return
    setScenarios(s => s.filter(sc => sc.id !== id))
  }

  const updateScenario = (id, field, value) => {
    setScenarios(s => s.map(sc => sc.id === id ? { ...sc, [field]: value } : sc))
  }

  const loanResults = useMemo(() =>
    loanScenarios.map(s => {
      const effDown = s.downPaymentMode === 'percent'
        ? Math.round(s.principal * s.downPaymentPercent / 100)
        : s.downPayment
      const effDownPct = s.principal > 0 ? (effDown / s.principal) * 100 : 0
      const isMortgage = LOAN_PRESETS[s.type]?.isMortgage
      const loanAmt = s.principal - effDown
      const effPmiRate = isMortgage && effDownPct >= 20 ? 0 : s.pmiRate
      const extras = isMortgage
        ? calculateMortgageExtras(s.principal, loanAmt > 0 ? loanAmt : 0, {
            propertyTaxRate: s.propertyTaxRate,
            pmiRate: effPmiRate,
            annualInsurance: s.annualInsurance,
            monthlyHOA: s.monthlyHOA,
          })
        : null
      const result = calculateLoanSummary(s.principal, s.rate, s.termMonths, s.extraPayment, effDown)
      const totalMonthly = extras
        ? result.monthlyPayment + extras.totalMonthlyExtras
        : result.monthlyPayment
      return { ...s, result, extras, totalMonthly, effectiveDownPayment: effDown, effectiveDownPaymentPercent: effDownPct }
    }),
    [loanScenarios]
  )

  const investmentResults = useMemo(() =>
    investmentScenarios.map(s => ({
      ...s,
      result: calculateInvestmentGrowth(s.initialAmount, s.monthlyContribution, s.annualReturn, s.years),
    })),
    [investmentScenarios]
  )

  const results = mode === 'loans' ? loanResults : investmentResults

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const m = 14
    let y = m

    const heading = (text, size = 16) => {
      doc.setFontSize(size)
      doc.setTextColor(0)
      doc.text(text, m, y)
      y += size === 16 ? 8 : 6
    }
    const subtext = (text) => {
      doc.setFontSize(9)
      doc.setTextColor(100)
      const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - m * 2)
      doc.text(lines, m, y)
      y += lines.length * 4 + 2
    }
    const checkPage = (need = 30) => {
      if (y + need > doc.internal.pageSize.getHeight() - m) {
        doc.addPage()
        y = m
      }
    }

    const title = mode === 'loans' ? 'Loan Comparison Report' : 'Investment Comparison Report'

    // ===== Title =====
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text(title, m, y + 10)
    y += 14
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}  |  Mode: ${mode === 'loans' ? 'Compare Loans' : 'Compare Investments'}`, m, y)
    y += 10

    if (mode === 'loans') {
      // ===== Scenario Inputs (one table per scenario) =====
      heading('Scenario Inputs')

      loanResults.forEach((s, i) => {
        checkPage(50)
        const scenarioName = s.name || `Scenario ${i + 1}`
        const isMortgage = LOAN_PRESETS[s.type]?.isMortgage

        doc.setFontSize(11)
        doc.setTextColor(0)
        doc.text(`${scenarioName}`, m, y)
        y += 6

        const rows = [
          ['Loan Type', LOAN_PRESETS[s.type]?.label || s.type],
          [isMortgage ? 'Home Price' : 'Loan Amount', formatCurrency(s.principal)],
          ['Interest Rate', `${s.rate}%`],
          ['Term', `${Math.floor(s.termMonths / 12)}y ${s.termMonths % 12}m (${s.termMonths} months)`],
          ['Down Payment', `${formatCurrency(s.effectiveDownPayment)} (${s.effectiveDownPaymentPercent.toFixed(1)}%)`],
          ['Extra Payment/mo', formatCurrencyExact(s.extraPayment)],
        ]
        if (isMortgage) {
          const effPmiRate = s.effectiveDownPaymentPercent >= 20 ? 0 : s.pmiRate
          rows.push(
            ['Property Tax Rate', `${s.propertyTaxRate}%`],
            ['PMI Rate', effPmiRate > 0 ? `${s.pmiRate}%` : `${s.pmiRate}% (waived -- 20%+ down)`],
            ['Annual Insurance', formatCurrency(s.annualInsurance)],
            ['Monthly HOA', formatCurrencyExact(s.monthlyHOA)],
          )
        }

        autoTable(doc, {
          body: rows,
          startY: y,
          theme: 'grid',
          styles: { fontSize: 8 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
        })
        y = doc.lastAutoTable.finalY + 6
      })
      y += 4

      // ===== Visual Comparison (bar metrics) =====
      checkPage(50)
      heading('Visual Comparison')
      subtext('Bar chart metrics comparing all scenarios at a glance.')

      const metricLabels = ['Monthly Payment', 'Total Interest Paid', 'Total Cost', 'Payoff Time']
      const metricData = [
        loanResults.map(s => formatCurrencyExact(s.totalMonthly)),
        loanResults.map(s => formatCurrency(s.result.totalInterest)),
        loanResults.map(s => formatCurrency(s.result.totalCost)),
        loanResults.map(s => `${Math.floor(s.result.actualTerm / 12)}y ${s.result.actualTerm % 12}m`),
      ]

      autoTable(doc, {
        head: [['Metric', ...loanResults.map((s, i) => s.name || `Scenario ${i + 1}`)]],
        body: metricLabels.map((label, idx) => [label, ...metricData[idx]]),
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { fontStyle: 'bold', halign: 'left', cellWidth: 40 } },
      })
      y = doc.lastAutoTable.finalY + 8

      // ===== Side-by-Side Results Table =====
      checkPage(60)
      heading('Side-by-Side Results')

      const sideHeaders = [['', ...loanResults.map((s, i) => s.name || `Scenario ${i + 1}`)]]
      const sideRows = [
        ['Loan Amount', ...loanResults.map(s => formatCurrency(s.result.loanAmount))],
        ['Down Payment', ...loanResults.map(s => `${formatCurrency(s.effectiveDownPayment)} (${s.effectiveDownPaymentPercent.toFixed(0)}%)`)],
        ['Interest Rate', ...loanResults.map(s => `${s.rate}%`)],
        ['Term', ...loanResults.map(s => `${Math.floor(s.termMonths / 12)}y ${s.termMonths % 12}m`)],
        ['P&I Payment', ...loanResults.map(s => formatCurrencyExact(s.result.monthlyPayment))],
      ]
      if (loanResults.some(s => s.extras)) {
        sideRows.push(['Tax + Ins + PMI + HOA', ...loanResults.map(s => s.extras ? formatCurrencyExact(s.extras.totalMonthlyExtras) : '-')])
      }
      sideRows.push(
        ['Total Monthly', ...loanResults.map(s => formatCurrencyExact(s.totalMonthly))],
        ['Total Interest', ...loanResults.map(s => formatCurrency(s.result.totalInterest))],
        ['Total Cost', ...loanResults.map(s => formatCurrency(s.result.totalCost))],
        ['Payoff Time', ...loanResults.map(s => `${Math.floor(s.result.actualTerm / 12)}y ${s.result.actualTerm % 12}m`)],
      )

      autoTable(doc, {
        head: sideHeaders,
        body: sideRows,
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
      })
      y = doc.lastAutoTable.finalY + 8

    } else {
      // ===== Investment Scenario Inputs =====
      heading('Scenario Inputs')

      investmentResults.forEach((s, i) => {
        checkPage(30)
        const scenarioName = s.name || `Scenario ${i + 1}`

        doc.setFontSize(11)
        doc.setTextColor(0)
        doc.text(`${scenarioName}`, m, y)
        y += 6

        autoTable(doc, {
          body: [
            ['Initial Amount', formatCurrency(s.initialAmount)],
            ['Monthly Contribution', formatCurrency(s.monthlyContribution)],
            ['Annual Return', `${s.annualReturn}%`],
            ['Time Horizon', `${s.years} years`],
          ],
          startY: y,
          theme: 'grid',
          styles: { fontSize: 8 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
        })
        y = doc.lastAutoTable.finalY + 6
      })
      y += 4

      // ===== Visual Comparison (bar metrics) =====
      checkPage(50)
      heading('Visual Comparison')
      subtext('Bar chart metrics comparing all scenarios at a glance.')

      const metricLabels = ['Final Balance', 'Total Growth', 'Total Contributed']
      const metricData = [
        investmentResults.map(s => formatCurrency(s.result.finalBalance)),
        investmentResults.map(s => formatCurrency(s.result.totalGrowth)),
        investmentResults.map(s => formatCurrency(s.result.totalContributions)),
      ]

      autoTable(doc, {
        head: [['Metric', ...investmentResults.map((s, i) => s.name || `Scenario ${i + 1}`)]],
        body: metricLabels.map((label, idx) => [label, ...metricData[idx]]),
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { fontStyle: 'bold', halign: 'left', cellWidth: 40 } },
      })
      y = doc.lastAutoTable.finalY + 8

      // ===== Side-by-Side Results Table =====
      checkPage(60)
      heading('Side-by-Side Results')

      const sideHeaders = [['', ...investmentResults.map((s, i) => s.name || `Scenario ${i + 1}`)]]
      const sideRows = [
        ['Initial Investment', ...investmentResults.map(s => formatCurrency(s.initialAmount))],
        ['Monthly Contribution', ...investmentResults.map(s => formatCurrency(s.monthlyContribution))],
        ['Annual Return', ...investmentResults.map(s => `${s.annualReturn}%`)],
        ['Time Horizon', ...investmentResults.map(s => `${s.years} years`)],
        ['Final Balance', ...investmentResults.map(s => formatCurrency(s.result.finalBalance))],
        ['Total Contributed', ...investmentResults.map(s => formatCurrency(s.result.totalContributions))],
        ['Total Growth', ...investmentResults.map(s => formatCurrency(s.result.totalGrowth))],
      ]

      autoTable(doc, {
        head: sideHeaders,
        body: sideRows,
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
      })
      y = doc.lastAutoTable.finalY + 8
    }

    // ===== Chart =====
    await addChartToPDF(doc, chartRef, { title: 'Visual Comparison' })

    doc.save(`${mode}-comparison-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Mode Toggle */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex rounded-xl border border-card overflow-hidden">
            {['loans', 'investments'].map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 py-2.5 text-sm font-medium transition ${
                  mode === m ? 'bg-active text-heading' : 'text-muted hover:text-heading'
                }`}
              >
                {m === 'loans' ? 'Compare Loans' : 'Compare Investments'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {scenarios.length < 4 && (
              <Button variant="secondary" size="sm" onClick={addScenario}>
                + Add Scenario
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
              Export PDF{!isPro ? ' (Pro)' : ''}
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Scenario Inputs */}
      <div className={`grid gap-4 ${scenarios.length <= 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-4'}`}>
        {scenarios.map((s, i) => (
          <GlassCard key={s.id} className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COMPARE_COLORS[i] }} />
                <input
                  value={s.name}
                  onChange={(e) => updateScenario(s.id, 'name', e.target.value)}
                  className="bg-transparent text-sm font-semibold text-heading focus:outline-none border-b border-transparent focus:border-card w-32"
                  placeholder={`Scenario ${i + 1}`}
                />
              </div>
              {scenarios.length > 2 && (
                <button onClick={() => removeScenario(s.id)} className="text-faint hover:text-negative transition p-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {mode === 'loans' ? (
              <LoanInputs scenario={s} onUpdate={(f, v) => updateScenario(s.id, f, v)} />
            ) : (
              <InvestmentInputs scenario={s} onUpdate={(f, v) => updateScenario(s.id, f, v)} />
            )}
          </GlassCard>
        ))}
      </div>

      {/* Visual Comparison */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Visual Comparison</h3>
        <div ref={chartRef} className="space-y-6">
          {mode === 'loans' ? (
            <>
              <ComparisonMetric
                label="Monthly Payment"
                items={loanResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.totalMonthly, color: COMPARE_COLORS[i] }))}
                format={formatCurrencyExact}
              />
              <ComparisonMetric
                label="Total Interest Paid"
                items={loanResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.totalInterest, color: COMPARE_COLORS[i] }))}
                format={formatCurrency}
              />
              <ComparisonMetric
                label="Total Cost"
                items={loanResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.totalCost, color: COMPARE_COLORS[i] }))}
                format={formatCurrency}
              />
              <ComparisonMetric
                label="Payoff Time"
                items={loanResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.actualTerm, color: COMPARE_COLORS[i] }))}
                format={v => `${Math.floor(v / 12)}y ${v % 12}m`}
              />
            </>
          ) : (
            <>
              <ComparisonMetric
                label="Final Balance"
                items={investmentResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.finalBalance, color: COMPARE_COLORS[i] }))}
                format={formatCurrency}
              />
              <ComparisonMetric
                label="Total Growth"
                items={investmentResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.totalGrowth, color: COMPARE_COLORS[i] }))}
                format={formatCurrency}
              />
              <ComparisonMetric
                label="Total Contributed"
                items={investmentResults.map((s, i) => ({ name: s.name || `Scenario ${i + 1}`, value: s.result.totalContributions, color: COMPARE_COLORS[i] }))}
                format={formatCurrency}
              />
            </>
          )}
        </div>
      </GlassCard>

      {/* Results Table */}
      <GlassCard className="!p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-card">
          <h3 className="text-sm font-semibold text-body">Side-by-Side Results</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted"></th>
                {results.map((s, i) => (
                  <th key={s.id} className="text-right px-4 py-3 text-xs font-semibold" style={{ color: COMPARE_COLORS[i] }}>
                    {s.name || `Scenario ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mode === 'loans' ? (
                <>
                  <CompRow label="Loan Amount" values={loanResults.map(s => formatCurrency(s.result.loanAmount))} />
                  <CompRow label="Down Payment" values={loanResults.map(s => `${formatCurrency(s.effectiveDownPayment)} (${s.effectiveDownPaymentPercent.toFixed(0)}%)`)} />
                  <CompRow label="Interest Rate" values={loanResults.map(s => `${s.rate}%`)} />
                  <CompRow label="Term" values={loanResults.map(s => `${Math.floor(s.termMonths / 12)}y ${s.termMonths % 12}m`)} />
                  <CompRow label="P&I Payment" values={loanResults.map(s => formatCurrencyExact(s.result.monthlyPayment))} />
                  {loanResults.some(s => s.extras) && (
                    <CompRow label="Tax + Ins + PMI + HOA" values={loanResults.map(s => s.extras ? formatCurrencyExact(s.extras.totalMonthlyExtras) : '-')} />
                  )}
                  <CompRow label="Total Monthly" values={loanResults.map(s => formatCurrencyExact(s.totalMonthly))} highlight />
                  <CompRow label="Total Interest" values={loanResults.map(s => formatCurrency(s.result.totalInterest))} />
                  <CompRow label="Total Cost" values={loanResults.map(s => formatCurrency(s.result.totalCost))} highlight />
                  <CompRow label="Payoff Time" values={loanResults.map(s => `${Math.floor(s.result.actualTerm / 12)}y ${s.result.actualTerm % 12}m`)} />
                </>
              ) : (
                <>
                  <CompRow label="Initial Investment" values={investmentResults.map(s => formatCurrency(s.initialAmount))} />
                  <CompRow label="Monthly Contribution" values={investmentResults.map(s => formatCurrency(s.monthlyContribution))} />
                  <CompRow label="Annual Return" values={investmentResults.map(s => `${s.annualReturn}%`)} />
                  <CompRow label="Time Horizon" values={investmentResults.map(s => `${s.years} years`)} />
                  <CompRow label="Final Balance" values={investmentResults.map(s => formatCurrency(s.result.finalBalance))} highlight />
                  <CompRow label="Total Contributed" values={investmentResults.map(s => formatCurrency(s.result.totalContributions))} />
                  <CompRow label="Total Growth" values={investmentResults.map(s => formatCurrency(s.result.totalGrowth))} highlight />
                </>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  )
}

function LoanInputs({ scenario, onUpdate }) {
  const isMortgage = LOAN_PRESETS[scenario.type]?.isMortgage
  const effDown = scenario.downPaymentMode === 'percent'
    ? Math.round(scenario.principal * scenario.downPaymentPercent / 100)
    : scenario.downPayment
  const effDownPct = scenario.principal > 0 ? (effDown / scenario.principal) * 100 : 0

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-muted mb-1">Type</label>
        <select
          value={scenario.type}
          onChange={(e) => {
            const p = LOAN_PRESETS[e.target.value]
            onUpdate('type', e.target.value)
            onUpdate('rate', p.defaultRate)
            onUpdate('termMonths', p.defaultTerm)
            if (p.isMortgage) {
              onUpdate('propertyTaxRate', 1.2)
              onUpdate('pmiRate', 0.75)
              onUpdate('annualInsurance', 1800)
            } else {
              onUpdate('propertyTaxRate', 0)
              onUpdate('pmiRate', 0)
              onUpdate('annualInsurance', 0)
              onUpdate('monthlyHOA', 0)
            }
          }}
          className="w-full bg-input border border-input rounded-xl px-3 py-2 text-sm text-heading focus:outline-none focus:ring-1 appearance-none cursor-pointer"
        >
          {Object.entries(LOAN_PRESETS).map(([key, p]) => (
            <option key={key} value={key} style={{ background: 'var(--page-bg)', color: 'var(--heading)' }}>
              {p.icon} {p.label}
            </option>
          ))}
        </select>
      </div>
      <CompactInput label={isMortgage ? 'Home Price' : 'Amount'} value={scenario.principal} onChange={v => onUpdate('principal', v)} prefix="$" />
      <CompactInput label="Rate" value={scenario.rate} onChange={v => onUpdate('rate', v)} suffix="%" step={0.1} />
      <CompactInput label="Term (months)" value={scenario.termMonths} onChange={v => onUpdate('termMonths', v)} />

      {/* Down payment with $/% toggle */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted w-24 shrink-0">
          Down {scenario.downPaymentMode === 'dollar' ? `(${effDownPct.toFixed(0)}%)` : `(${formatCurrency(effDown)})`}
        </label>
        <div className="relative flex-1">
          <div className="flex gap-1">
            <div className="relative flex-1">
              {scenario.downPaymentMode === 'dollar' ? (
                <>
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-faint">$</span>
                  <input
                    type="number"
                    value={scenario.downPayment}
                    onChange={(e) => onUpdate('downPayment', parseFloat(e.target.value) || 0)}
                    min={0}
                    className="w-full bg-input border border-input rounded-lg py-1.5 text-sm text-heading focus:outline-none focus:ring-1 pl-6 pr-2"
                  />
                </>
              ) : (
                <>
                  <input
                    type="number"
                    value={scenario.downPaymentPercent}
                    onChange={(e) => onUpdate('downPaymentPercent', parseFloat(e.target.value) || 0)}
                    step={0.5}
                    min={0}
                    max={100}
                    className="w-full bg-input border border-input rounded-lg py-1.5 text-sm text-heading focus:outline-none focus:ring-1 pl-2.5 pr-6"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-faint">%</span>
                </>
              )}
            </div>
            <div className="flex rounded-lg border border-card overflow-hidden shrink-0">
              <button
                onClick={() => {
                  onUpdate('downPaymentMode', 'dollar')
                  onUpdate('downPayment', effDown)
                }}
                className={`px-1.5 py-1 text-xs font-medium transition ${
                  scenario.downPaymentMode === 'dollar' ? 'bg-active text-heading' : 'text-faint'
                }`}
              >$</button>
              <button
                onClick={() => {
                  onUpdate('downPaymentMode', 'percent')
                  onUpdate('downPaymentPercent', scenario.principal > 0 ? Math.round((scenario.downPayment / scenario.principal) * 1000) / 10 : 0)
                }}
                className={`px-1.5 py-1 text-xs font-medium transition ${
                  scenario.downPaymentMode === 'percent' ? 'bg-active text-heading' : 'text-faint'
                }`}
              >%</button>
            </div>
          </div>
        </div>
      </div>

      <CompactInput label="Extra Payment" value={scenario.extraPayment} onChange={v => onUpdate('extraPayment', v)} prefix="$" />

      {isMortgage && (
        <>
          <div className="border-t border-subtle pt-2 mt-1">
            <p className="text-xs text-faint mb-2">
              Mortgage Costs
              {effDownPct >= 20 && <span className="text-positive ml-1">(No PMI)</span>}
            </p>
          </div>
          <CompactInput label="Tax Rate" value={scenario.propertyTaxRate} onChange={v => onUpdate('propertyTaxRate', v)} suffix="%" step={0.1} />
          <CompactInput label="PMI Rate" value={scenario.pmiRate} onChange={v => onUpdate('pmiRate', v)} suffix="%" step={0.05} />
          <CompactInput label="Insurance/yr" value={scenario.annualInsurance} onChange={v => onUpdate('annualInsurance', v)} prefix="$" />
          <CompactInput label="HOA/mo" value={scenario.monthlyHOA} onChange={v => onUpdate('monthlyHOA', v)} prefix="$" />
        </>
      )}
    </div>
  )
}

function InvestmentInputs({ scenario, onUpdate }) {
  return (
    <div className="space-y-3">
      <CompactInput label="Initial Amount" value={scenario.initialAmount} onChange={v => onUpdate('initialAmount', v)} prefix="$" />
      <CompactInput label="Monthly" value={scenario.monthlyContribution} onChange={v => onUpdate('monthlyContribution', v)} prefix="$" />
      <CompactInput label="Return" value={scenario.annualReturn} onChange={v => onUpdate('annualReturn', v)} suffix="%" step={0.1} />
      <CompactInput label="Years" value={scenario.years} onChange={v => onUpdate('years', v)} min={1} max={50} />
    </div>
  )
}

function CompactInput({ label, value, onChange, prefix, suffix, step, min, max }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-muted w-24 shrink-0">{label}</label>
      <div className="relative flex-1">
        {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-faint">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-lg py-1.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix ? 'pl-6 pr-2' : suffix ? 'pl-2.5 pr-8' : 'px-2.5'
          }`}
        />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
      </div>
    </div>
  )
}

function ComparisonMetric({ label, items, format }) {
  const maxVal = Math.max(...items.map(i => i.value), 1)
  const minVal = Math.min(...items.map(i => i.value))
  const best = items.reduce((a, b) => a.value <= b.value ? a : b)

  return (
    <div>
      <p style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', color: 'var(--muted)' }}>{label}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((item) => {
          const pct = maxVal > 0 ? (item.value / maxVal) * 100 : 0
          const isBest = items.length > 1 && item.value === best.value && minVal !== maxVal
          return (
            <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)', width: '96px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
              <div style={{ flex: 1, height: '32px', borderRadius: '8px', backgroundColor: 'var(--subtle-bg, rgba(128,128,128,0.1))', position: 'relative' }}>
                <div
                  style={{
                    height: '32px',
                    borderRadius: '8px',
                    width: `${Math.max(pct, 6)}%`,
                    backgroundColor: item.color,
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#ffffff', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    {format(item.value)}
                  </span>
                </div>
              </div>
              {isBest && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--positive)', whiteSpace: 'nowrap' }}>Best</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CompRow({ label, values, highlight }) {
  return (
    <tr className={`border-b border-subtle ${highlight ? 'bg-subtle' : ''}`}>
      <td className="px-4 py-2.5 text-sm text-muted">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`px-4 py-2.5 text-sm text-right ${highlight ? 'font-semibold text-heading' : 'text-body'}`}>
          {v}
        </td>
      ))}
    </tr>
  )
}
